<template>
  <vs-popup
    title="Complete seu cadastro"
    :active.sync="innerActive"
    @close="onClose"
  >
    <p class="social-modal__intro">
      Para continuar com o Google, informe seu CPF e telefone com os mesmos dados do cadastro
      convencional.
    </p>
    <ValidationObserver ref="observer" tag="form" v-slot="{ invalid }" @submit.prevent="submit">
      <ValidationProvider name="cpf" rules="required|cpf" v-slot="{ errors }">
        <vs-input
          v-model="cpf"
          class="social-modal__input"
          label-placeholder="CPF"
          placeholder="000.000.000-00"
          v-mask="'###.###.###-##'"
          :danger="Boolean(errors.length)"
          :danger-text="errors[0]"
        />
      </ValidationProvider>
      <VuePhoneNumberInput
        id="social-reg-phone"
        default-country-code="BR"
        v-model="telephone"
        valid-color="#000000"
        class="social-modal__phone"
        :translations="{
          countrySelectorLabel: 'Código',
          countrySelectorError: 'Selecione um País',
          phoneNumberLabel: 'Número de Telefone',
          example: 'Exemplo:',
        }"
      />
      <span v-if="phoneError" class="social-modal__error">{{ phoneError }}</span>
      <span v-if="apiError" class="social-modal__error">{{ apiError }}</span>
      <vs-button
        type="button"
        class="social-modal__submit bg-primary"
        :disabled="invalid || submitting"
        @click="submit"
      >
        Continuar
      </vs-button>
    </ValidationObserver>
  </vs-popup>
</template>

<script>
import VuePhoneNumberInput from "vue-phone-number-input";
import "vue-phone-number-input/dist/vue-phone-number-input.css";
import { ValidationProvider, ValidationObserver, Validator } from "vee-validate";
import api from "@/services/api";

export default {
  name: "SocialLoginRegistrationModal",
  components: {
    ValidationProvider,
    ValidationObserver,
    VuePhoneNumberInput,
  },
  props: {
    active: { type: Boolean, default: false },
    socialToken: { type: String, default: null },
  },
  data() {
    return {
      innerActive: false,
      cpf: "",
      telephone: "",
      phoneError: "",
      apiError: "",
      submitting: false,
    };
  },
  watch: {
    active(v) {
      this.innerActive = v;
      if (v) {
        this.resetFields();
      }
    },
    innerActive(v) {
      this.$emit("update:active", v);
    },
  },
  created() {
    this.ensureCpfRule();
  },
  methods: {
    ensureCpfRule() {
      if (Validator.rules && Object.prototype.hasOwnProperty.call(Validator.rules, "cpf")) {
        return;
      }
      Validator.extend("cpf", {
        getMessage: (field) => `O ${field} informado não é válido`,
        validate: (value) => {
          if (!value || typeof value !== "string") return false;
          const cpfReg = /[0-9]{3}\.[0-9]{3}\.[0-9]{3}-?[0-9]{2}/;
          if (!value.match(cpfReg)) return false;
          const replaced = value.replace(/\./g, "").replace(/-/g, "");
          let sum = 0;
          let rest = 0;
          for (let i = 1; i <= 9; i++)
            sum += parseInt(replaced.substring(i - 1, i), 10) * (11 - i);
          rest = (sum * 10) % 11;
          if (rest === 10 || rest === 11) rest = 0;
          if (rest !== parseInt(replaced.substring(9, 10), 10)) return false;
          sum = 0;
          for (let i = 1; i <= 10; i++)
            sum += parseInt(replaced.substring(i - 1, i), 10) * (12 - i);
          rest = (sum * 10) % 11;
          if (rest === 10 || rest === 11) rest = 0;
          if (rest !== parseInt(replaced.substring(10, 11), 10)) return false;
          return true;
        },
      });
    },
    resetFields() {
      this.cpf = "";
      this.telephone = "";
      this.phoneError = "";
      this.apiError = "";
      this.submitting = false;
    },
    onClose() {
      this.$emit("update:active", false);
    },
    validatePhoneDigits() {
      const d = String(this.telephone || "").replace(/\D/g, "");
      return d.length >= 10 && d.length <= 13;
    },
    async submit() {
      this.apiError = "";
      this.phoneError = "";
      const ok = await this.$refs.observer.validate();
      if (!ok) return;
      if (!this.validatePhoneDigits()) {
        this.phoneError = "Informe um telefone válido.";
        return;
      }
      if (!this.socialToken) {
        this.apiError = "Sessão expirada. Abra o login com Google novamente.";
        return;
      }
      this.submitting = true;
      try {
        const { data } = await api.post(
          "/api/v2/auth/social-login/complete-registration",
          {
            social_token: this.socialToken,
            cpf: this.cpf,
            telephone: this.telephone,
          }
        );
        this.innerActive = false;
        this.$emit("update:active", false);
        this.$emit("completed", {
          token: data.token,
          created_account: data.created_account === true,
          application_id:
            data.application_id != null ? data.application_id : null,
          company_label:
            data.company_label != null ? data.company_label : null,
          vacancy_id: data.vacancy_id != null ? data.vacancy_id : null,
        });
      } catch (e) {
        const res = e.response;
        const errData = res && res.data;
        const codes = errData && errData.errors && errData.errors._error_code;
        if (
          res &&
          res.status === 422 &&
          Array.isArray(codes) &&
          codes.includes("cpf_already_exists")
        ) {
          this.apiError =
            "Este CPF já está cadastrado. Faça login com sua conta existente.";
        } else if (errData && errData.errors) {
          const first =
            errData.errors.cpf && errData.errors.cpf[0]
              ? errData.errors.cpf[0]
              : errData.errors.social_token && errData.errors.social_token[0]
                ? errData.errors.social_token[0]
                : errData.message || "Não foi possível concluir o cadastro.";
          this.apiError = first;
        } else {
          this.apiError = "Não foi possível concluir o cadastro.";
        }
      } finally {
        this.submitting = false;
      }
    },
  },
};
</script>

<style scoped>
.social-modal__intro {
  font-size: 14px;
  margin-bottom: 16px;
  line-height: 1.4;
}
.social-modal__input {
  width: 100%;
  margin-bottom: 12px;
}
.social-modal__phone {
  margin-top: 8px;
  margin-bottom: 8px;
}
.social-modal__error {
  display: block;
  color: #ea5455;
  font-size: 12px;
  margin-bottom: 8px;
}
.social-modal__submit {
  width: 100%;
  margin-top: 16px;
}
</style>
